import { useEffect, useRef } from 'react';
import { normalizeEmail } from '../lib/emailUtils';
import { getDbSupabase, isSupabaseConfigured, supabase } from '../lib/supabase';

const DEBOUNCE_MS = 300;

type UsePortalAllowlistRealtimeOptions = {
  enabled?: boolean;
  /** When set, only receive changes for this email (affected user session). */
  emailFilter?: string | null;
  onChange?: () => void;
};

/**
 * Debounced refresh when portal membership / shared profile rows change.
 * Source of truth: access_management.portal_access (+ CRM profiles for labels).
 */
export function usePortalAllowlistRealtime(options: UsePortalAllowlistRealtimeOptions = {}) {
  const { enabled = true, emailFilter = null, onChange } = options;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !isSupabaseConfigured) return undefined;

    const schedule = () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        onChangeRef.current?.();
      }, DEBOUNCE_MS);
    };

    const filterEmail = emailFilter ? normalizeEmail(emailFilter) : null;
    const filter = filterEmail ? `email=eq.${filterEmail}` : undefined;
    const channelName = filterEmail
      ? `portal-allowlist-self:${filterEmail}`
      : 'portal-allowlist-admin';

    const channels: ReturnType<NonNullable<typeof supabase>['channel']>[] = [];

    if (supabase) {
      let wide = supabase.channel(`${channelName}:access`);
      wide = wide.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'access_management',
          table: 'portal_access',
          ...(filter ? { filter } : {}),
        },
        schedule,
      );
      wide.subscribe();
      channels.push(wide);
    }

    const crm = getDbSupabase('crm');
    let crmChannel = crm.channel(`${channelName}:profiles`);
    crmChannel = crmChannel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'crm',
        table: 'portal_user_profiles',
        ...(filter ? { filter } : {}),
      },
      schedule,
    );
    crmChannel.subscribe();
    channels.push(crmChannel);

    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      for (const channel of channels) {
        void channel.unsubscribe();
      }
    };
  }, [enabled, emailFilter]);
}
